import React, { useMemo, useState } from "react";
import { Info, Sparkles } from "lucide-react";
import { formatINR } from "../../lib/format";
import { computeEstimate } from "../../lib/estimate";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";

const INCLUDED_BULLETS = [
  "Hours of coverage scoped to your event",
  "Curated edited photos delivered online",
  "ShutterKART Support — single point of contact",
  "Same-day highlight reels (where applicable)",
  "Custom add-ons: drone, full-length film, premium album",
];

/**
 * Estimate widget — sticky live pricing. Reads pricingRules from caller
 * (parent Plan owns the cached fetch).
 */
export default function EstimateWidget({ payload, pricingRules, compact }) {
  const range = useMemo(
    () => computeEstimate(payload, pricingRules),
    [payload, pricingRules],
  );
  const [pulseKey, setPulseKey] = useState(0);

  // Bump pulse animation on range change
  const lastRange = React.useRef(null);
  React.useEffect(() => {
    if (!range) return;
    if (
      !lastRange.current ||
      lastRange.current.min !== range.min ||
      lastRange.current.max !== range.max
    ) {
      lastRange.current = range;
      setPulseKey((k) => k + 1);
    }
  }, [range]);

  return (
    <aside
      data-testid="estimate-widget"
      className={[
        "rounded-2xl border border-[#EAE3D2] bg-white shadow-sm",
        compact ? "p-4" : "p-6",
      ].join(" ")}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#C8A24B]">
        <Sparkles className="h-3 w-3" />
        Estimated range
      </div>

      {range ? (
        <div className="mt-3">
          <p
            key={pulseKey}
            data-testid="estimate-range-text"
            className="animate-pulse-once font-display text-2xl text-[#1A1A1A] sm:text-3xl"
          >
            {formatINR(range.min)} – {formatINR(range.max)}
          </p>
          <p className="mt-2 text-xs text-[#1A1A1A]/55">
            Final pricing depends on details our team confirms.
          </p>
          <p
            data-testid="estimate-inclusive-note"
            className="mt-1 text-[10px] italic text-[#1A1A1A]/45"
          >
            *Inclusive of GST*
          </p>
        </div>
      ) : (
        <p
          data-testid="estimate-empty"
          className="mt-3 text-sm text-[#1A1A1A]/65"
        >
          Add a few details to see your estimate.
        </p>
      )}

      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            data-testid="estimate-included-trigger"
            className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/70 transition hover:text-[#C8A24B]"
          >
            <Info className="h-3.5 w-3.5" />
            What is typically included?
          </button>
        </DialogTrigger>
        <DialogContent className="bg-[#FAF7F2]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A1A1A]">
              What we usually cover
            </DialogTitle>
          </DialogHeader>
          <ul className="mt-2 space-y-2 text-sm text-[#1A1A1A]/80">
            {INCLUDED_BULLETS.map((line, i) => (
              <li key={i} className="flex items-start gap-3 border-b border-[#EAE3D2] pb-2 last:border-b-0">
                <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#C8A24B]" />
                {line}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-[#1A1A1A]/55">
            This isn’t a quote — it’s a quick guide. Submit your brief and our
            team will craft a precise quote within 24 hours.
          </p>
        </DialogContent>
      </Dialog>
    </aside>
  );
}
